import { Plus } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import type { CompanyModuleAccessHook } from '../../utils/useCompanyModuleAccess';
import useCompanyModuleAccess from '../../utils/useCompanyModuleAccess';
import type { ResultsProgramItemDataFragment } from '../AssessmentResultsPage/shared/Result.api';
import { useFetchResultsProgramsQuery } from '../AssessmentResultsPage/shared/Result.api';
import BmpPerformanceTab from './Bmp/BmpPerformanceTab';
import FinSwimPerformanceTab from './FinSwim/FinSwimPerformanceTab';
import IpptPerformanceTab from './Ippt/IpptPerformanceTab';
import March20kmPerformanceTab from './March20km/March20kmPerformanceTab';
import { useUsersForPerformanceResultQuery } from './PerformanceResult.api';
import SocPerformanceTab from './Soc/SocPerformanceTab';
import WeightPerformanceTab from './Weight/WeightPerformanceTab';
import CreatePerformanceResultModal from './shared/CreatePerformanceResultModal';
import PerformanceResultListFilter from './shared/PerformanceResultListFilter';
import BulkModificationModal from './shared/bulk/BulkModificationModal';
import { buildAssessmentRunOptions, type PerformanceFilterState, type PerformanceTabRef } from './shared/utils';
import { AssessmentRunStatus, PerformanceResultType, UserRole } from '@/apolloClient.types';
import useAppAbilities from '@/components/abilities';
import NotFound from '@/components/blocks/NotFound';
import Loader from '@/components/common/Loader';
import PageHeader from '@/components/common/PageHeader';
import Tab from '@/components/common/Tab';
import makeUseTabs from '@/components/common/Tab/makeUseTabs';
import { useGetCompanyId } from '@/contexts/UserSession';

const defaultPerformanceTabs: { key: PerformanceResultType; identifier: string; labelKey: string }[] = [
    { key: PerformanceResultType.Weight, identifier: 'weight', labelKey: 'tabs.weight' },
    { key: PerformanceResultType.Ippt, identifier: 'ippt', labelKey: 'tabs.ippt' },
    { key: PerformanceResultType.Soc, identifier: 'soc', labelKey: 'tabs.soc' },
    { key: PerformanceResultType.March20km, identifier: '20km-road-march', labelKey: 'tabs.20kmCombatMarch' },
    { key: PerformanceResultType.FinSwim, identifier: 'fin-swim', labelKey: 'tabs.finSwim' },
    { key: PerformanceResultType.Bmp, identifier: 'bmp', labelKey: 'tabs.bmp' },
];

const useViewResultTabs = makeUseTabs((moduleAccess: CompanyModuleAccessHook) => {
    const { t } = useTranslation('performanceResult');

    return useMemo(() => {
        if (moduleAccess.loading) {
            return [{ name: t('tabs.loading', 'Loading'), key: 'loading' }];
        }

        const tabs = defaultPerformanceTabs
            .filter(tab => moduleAccess.has(tab.identifier))
            .map(tab => ({ name: t(tab.labelKey), key: tab.key }));

        return tabs.length > 0 ? tabs : [{ name: t('tabs.unavailable', 'Unavailable'), key: 'unavailable' }];
    }, [moduleAccess, t]);
}, true);

const tabsComponent = {
    [PerformanceResultType.Weight]: WeightPerformanceTab,
    [PerformanceResultType.Ippt]: IpptPerformanceTab,
    [PerformanceResultType.Soc]: SocPerformanceTab,
    [PerformanceResultType.March20km]: March20kmPerformanceTab,
    [PerformanceResultType.FinSwim]: FinSwimPerformanceTab,
    [PerformanceResultType.Bmp]: BmpPerformanceTab,
};

const PerformanceResultPage = () => {
    const { assessmentRunId: defaultAssessmentRunId } = useParams<{
        assessmentRunId?: string;
    }>();

    const { t } = useTranslation('performanceResult');
    const abilities = useAppAbilities();
    const selectedCompanyId = useGetCompanyId();
    const [openBulkModificationModal, setOpenBulkModificationModal] = useState(false);
    const [openCreateModal, setOpenCreateModal] = useState(false);

    const [filterState, setFilterState] = useState<PerformanceFilterState>({
        traineeName: null,
        programId: null,
        assessmentRunId: defaultAssessmentRunId ?? null,
        groupId: null,
        subGroupId: null,
        filterAssessmentRunIds: defaultAssessmentRunId ? [defaultAssessmentRunId] : [],
    });

    const userRequestFilter = useMemo(() => {
        return {
            groupIds: filterState.groupId ? [filterState.groupId] : null,
        };
    }, [filterState.groupId]);

    const { data: usersData } = useUsersForPerformanceResultQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                companyId: selectedCompanyId,
                groupIds: userRequestFilter.groupIds,
                role: UserRole.Trainee,
            },
        },
        skip: !selectedCompanyId,
    });

    const { data: programsData } = useFetchResultsProgramsQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            companyId: selectedCompanyId || '',
            filter: { includeAssessmentRunStatuses: [AssessmentRunStatus.Ended, AssessmentRunStatus.InProgress] },
        },
        skip: !selectedCompanyId,
    });

    const programs: ResultsProgramItemDataFragment[] = useMemo(
        () => programsData?.programs?.items || [],
        [programsData?.programs?.items]
    );

    const assessmentRunOptions = useMemo(
        () =>
            buildAssessmentRunOptions(programs, {
                groupId: filterState.groupId,
                subGroupId: filterState.subGroupId,
            }),
        [filterState.groupId, filterState.subGroupId, programs]
    );
    const tabRef = useRef<PerformanceTabRef>(null);

    const moduleAccess = useCompanyModuleAccess(selectedCompanyId);

    const { tabs, activeTab, onTabChange } = useViewResultTabs(moduleAccess);

    const ActiveTabComponent = useMemo(() => {
        return tabsComponent[activeTab as keyof typeof tabsComponent];
    }, [activeTab]);

    const onFilterChanges = useCallback((state: PerformanceFilterState) => {
        setFilterState(state);
    }, []);

    const showModal = useCallback(() => {
        setOpenCreateModal(true);
    }, []);

    const createModal = useMemo(() => {
        return (
            <CreatePerformanceResultModal
                open={openCreateModal}
                onClose={refresh => {
                    if (refresh) {
                        tabRef.current?.refetch();
                    }
                    setOpenCreateModal(false);
                }}
                groupUsers={usersData?.users.items || []}
                resultType={activeTab as PerformanceResultType}
                assessmentRunOptions={assessmentRunOptions}
            />
        );
    }, [openCreateModal, usersData, activeTab, assessmentRunOptions]);

    const showBulkModificationModal = useCallback(() => {
        setOpenBulkModificationModal(true);
    }, []);

    const bulkModificationModal = useMemo(() => {
        if (!openBulkModificationModal || !activeTab) {
            return null;
        }
        return (
            <BulkModificationModal
                open={openBulkModificationModal}
                onClose={refresh => {
                    if (refresh) {
                        tabRef.current?.refetch();
                    }
                    setOpenBulkModificationModal(false);
                }}
                resultType={activeTab as PerformanceResultType}
                exportFilters={{
                    assessmentRunIds: filterState.filterAssessmentRunIds.length
                        ? filterState.filterAssessmentRunIds
                        : undefined,
                    groupIds: filterState.groupId ? [filterState.groupId] : undefined,
                    subgroupIds: filterState.subGroupId ? [filterState.subGroupId] : undefined,
                    searchText: filterState.traineeName || undefined,
                }}
            />
        );
    }, [openBulkModificationModal, activeTab, filterState]);

    const filteredTabs = useMemo(() => {
        return tabs;
    }, [tabs]);

    const hasValidTab = filteredTabs.some(tab => tab.key in tabsComponent);

    if (!abilities.canViewPerformanceResults() || (!moduleAccess.loading && !hasValidTab)) {
        return <NotFound />;
    }

    if (moduleAccess?.loading) {
        return <Loader />;
    }

    return (
        <div className="flex w-full flex-col gap-5 lg:gap-10">
            <PageHeader
                title={t('title', 'Performance Results')}
                actionButton={{
                    label: t('actions.addNewRecord'),
                    onClick: showModal,
                    icon: <Plus aria-hidden="true" className="size-4" />,
                    show: abilities.canManagePerformanceResultsForCompany(selectedCompanyId || ''),
                }}
                subActionButton={
                    abilities.canImportExportPerformanceResultsForCompany()
                        ? {
                              label: t('actions.bulkModification'),
                              onClick: showBulkModificationModal,
                          }
                        : undefined
                }
                noControlsRowGap
            />

            <div className="grid gap-5">
                <PerformanceResultListFilter filterState={filterState} callback={onFilterChanges} />

                <div className="overflow-x-auto dark:border-gray-700">
                    <Tab tabs={filteredTabs} onChange={onTabChange} variant="pills" />
                </div>
            </div>

            <div>
                {ActiveTabComponent && (
                    <ActiveTabComponent
                        ref={tabRef}
                        filterState={filterState}
                        assessmentRunOptions={assessmentRunOptions}
                    />
                )}
            </div>

            {createModal}
            {bulkModificationModal}
        </div>
    );
};

export default PerformanceResultPage;
